import { useEffect, useState } from "react";
import { IndianRupee, Coins, Flame, Ticket, Bot, Wallet, TrendingUp, Percent, CreditCard, Package, Users, Settings, Download, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import api, { errMsg, fmtINR, fmtDateTime } from "../../lib/api";
import { StatCard, PageHeader, StatusBadge, TableShell, TR, TD, LoadingRows, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const TABS = [
  { k: "dashboard", l: "AI Dashboard", icon: TrendingUp },
  { k: "purchases", l: "Credit Purchases", icon: CreditCard },
  { k: "packages", l: "Credit Packages", icon: Package },
  { k: "transactions", l: "AI Transactions", icon: Bot },
  { k: "tenants", l: "Customer Usage", icon: Users },
  { k: "settings", l: "AI Settings", icon: Settings },
];
const RANGES = [["today", "Today"], ["7d", "Last 7 Days"], ["month", "This Month"], ["last_month", "Last Month"], ["year", "This Year"], ["all", "All Time"]];

export default function RootAi() {
  const [tab, setTab] = useState("dashboard");
  const [range, setRange] = useState("month");
  const [an, setAn] = useState(null);
  const [purchases, setPurchases] = useState(null);
  const [pFilter, setPFilter] = useState("pending");
  const [packages, setPackages] = useState(null);
  const [pkgForm, setPkgForm] = useState({ name: "", credits: "", price: "" });
  const [txns, setTxns] = useState(null);
  const [txnStatus, setTxnStatus] = useState("");
  const [tenants, setTenants] = useState(null);
  const [settings, setSettings] = useState(null);
  const [grant, setGrant] = useState({ email: "", credits: "", note: "" });
  const [busy, setBusy] = useState(false);

  const loadAn = (r = range) => api.get(`/root/ai/analytics?range=${r}`).then((x) => setAn(x.data)).catch((e) => toast.error(errMsg(e)));
  const loadPurchases = (f = pFilter) => api.get(`/root/ai/purchases${f ? `?status=${f}` : ""}`).then((x) => setPurchases(x.data.purchases)).catch((e) => toast.error(errMsg(e)));
  const loadPackages = () => api.get("/root/ai/packages").then((x) => setPackages(x.data.packages)).catch((e) => toast.error(errMsg(e)));
  const loadTxns = (s = txnStatus) => api.get(`/root/ai/transactions${s ? `?status=${s}` : ""}`).then((x) => setTxns(x.data.transactions)).catch((e) => toast.error(errMsg(e)));

  useEffect(() => { loadAn(); loadPackages(); }, []);
  useEffect(() => {
    if (tab === "dashboard") loadAn();
    if (tab === "purchases") loadPurchases();
    if (tab === "transactions") loadTxns();
    if (tab === "tenants" && !tenants) api.get("/root/ai/tenants").then((x) => setTenants(x.data.tenants)).catch((e) => toast.error(errMsg(e)));
    if (tab === "settings" && !settings) api.get("/root/ai/settings").then((x) => setSettings(x.data)).catch((e) => toast.error(errMsg(e)));
  }, [tab]);

  const approve = async (p) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/root/ai/purchases/${p.id}/approve`);
      toast.success(`${data.credits} credits added to ${p.owner_name}`);
      loadPurchases(); loadAn(); loadPackages();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reject = async (p) => {
    const note = window.prompt("Reason for rejection (shown to customer):") || "";
    setBusy(true);
    try {
      await api.post(`/root/ai/purchases/${p.id}/reject`, { note });
      toast.success("Purchase rejected");
      loadPurchases();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const createPkg = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/root/ai/packages", { ...pkgForm, credits: Number(pkgForm.credits), price: Number(pkgForm.price) });
      toast.success("Package created"); setPkgForm({ name: "", credits: "", price: "" }); loadPackages();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  const togglePkg = async (p) => {
    await api.patch(`/root/ai/packages/${p.id}`, { active: !p.active }).then(() => loadPackages()).catch((e) => toast.error(errMsg(e)));
  };
  const saveSettings = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...settings };
      if (typeof payload.model_pricing === "string") payload.model_pricing = JSON.parse(payload.model_pricing || "{}");
      const { data } = await api.put("/root/ai/settings", payload);
      setSettings(data); toast.success("AI settings saved");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  const doGrant = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/root/ai/credits/grant", { ...grant, credits: Number(grant.credits) });
      toast.success(`Credits adjusted — new balance ${data.balance}`);
      setGrant({ email: "", credits: "", note: "" }); setTenants(null);
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  const exportCsv = (report) => {
    const t = localStorage.getItem("zylo_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/root/ai/export.csv?report=${report}`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => r.blob()).then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b); a.download = `ai_${report}.csv`; a.click();
      }).catch(() => toast.error("Export failed"));
  };

  return (
    <div data-testid="root-ai-page">
      <PageHeader title="AI Credits & Revenue" subtitle="MILTROO AI business — credit sales, provider costs, gross profit and usage analytics." testid="root-ai-title" />

      <div className="flex gap-2 flex-wrap mb-8">
        {TABS.map((t) => (
          <button key={t.k} data-testid={`ai-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold border transition-colors ${tab === t.k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="h-4 w-4" /> {t.l}
          </button>
        ))}
      </div>

      {tab === "dashboard" && (
        !an ? <div className="bg-card border border-border rounded-2xl p-8"><LoadingRows rows={4} /></div> : (
          <div className="space-y-10">
            <div className="flex gap-2 flex-wrap">
              {RANGES.map(([k, l]) => (
                <button key={k} data-testid={`ai-range-${k}`} onClick={() => { setRange(k); setAn(null); loadAn(k); }}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-colors ${range === k ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>{l}</button>
              ))}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              <StatCard icon={IndianRupee} label="AI Credit Revenue" value={fmtINR(an.revenue)} testid="ai-stat-revenue" />
              <StatCard icon={Coins} label="Credits Sold" value={an.credits_sold} sub={`${an.credits_used} used · ${an.credits_remaining} remaining`} testid="ai-stat-sold" delay={0.05} />
              <StatCard icon={Bot} label="AI Requests" value={an.requests} sub={`${an.failed} failed (not charged)`} testid="ai-stat-requests" delay={0.1} />
              <StatCard icon={Wallet} label="AI Provider Cost" value={fmtINR(an.cost_inr)} sub="USD→INR tracked per request" testid="ai-stat-cost" delay={0.15} />
              <StatCard icon={TrendingUp} label="Gross AI Profit" value={fmtINR(an.profit)} testid="ai-stat-profit" delay={0.2} tint="bg-emerald-500/10 text-emerald-600" />
              <StatCard icon={Percent} label="Gross Margin" value={`${an.margin}%`} sub="Before gateway/tax/infra costs" testid="ai-stat-margin" delay={0.25} tint="bg-emerald-500/10 text-emerald-600" />
              <StatCard icon={Ticket} label="Avg Credit Price" value={`₹${an.avg_credit_price}`} testid="ai-stat-avg" delay={0.3} />
              <StatCard icon={Flame} label="Top Feature" value={Object.entries(an.by_feature).sort((a, b) => b[1].credits - a[1].credits)[0]?.[0]?.replace(/_/g, " ") || "—"} testid="ai-stat-top" delay={0.35} />
            </div>

            <div className="bg-card border border-border rounded-2xl p-6">
              <p className="font-display font-bold text-xl tracking-tight mb-4">Revenue vs AI Cost (daily)</p>
              {an.series.length === 0 ? <p className="text-sm text-muted-foreground">No activity in this range yet.</p> : (
                <div className="h-64" data-testid="ai-revenue-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={an.series}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="revenue" name="Revenue ₹" stroke="#10b981" fill="#10b981" fillOpacity={0.15} />
                      <Area type="monotone" dataKey="cost" name="AI Cost ₹" stroke="#ef4444" fill="#ef4444" fillOpacity={0.12} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div>
                <p className="font-display font-bold text-xl tracking-tight mb-4">Model-wise Cost</p>
                {Object.keys(an.by_model).length === 0 ? <EmptyState icon={Bot} title="No AI usage yet" body="Model analytics appear after the first AI request." /> : (
                  <TableShell head={["Model", "Requests", "Tokens In/Out", "Cost", "Revenue Attr.", "Margin"]} testid="ai-by-model">
                    {Object.entries(an.by_model).map(([m, b]) => (
                      <TR key={m} testid={`model-${m}`}>
                        <TD className="font-semibold">{m}</TD>
                        <TD>{b.requests}</TD>
                        <TD className="text-xs text-muted-foreground">{b.input_tokens.toLocaleString()} / {b.output_tokens.toLocaleString()}</TD>
                        <TD>{fmtINR(b.cost_inr)}</TD>
                        <TD>{fmtINR(b.revenue_attributed)}</TD>
                        <TD className={b.margin >= 0 ? "text-emerald-600 font-bold" : "text-red-500 font-bold"}>{b.margin}%</TD>
                      </TR>
                    ))}
                  </TableShell>
                )}
              </div>
              <div>
                <p className="font-display font-bold text-xl tracking-tight mb-4">Feature Profitability</p>
                {Object.keys(an.by_feature).length === 0 ? <EmptyState icon={TrendingUp} title="No AI usage yet" body="Feature analytics appear after the first AI request." /> : (
                  <TableShell head={["Feature", "Requests", "Credits", "Cost", "Revenue Attr.", "Profit"]} testid="ai-by-feature">
                    {Object.entries(an.by_feature).map(([f, b]) => (
                      <TR key={f} testid={`feature-${f}`}>
                        <TD className="font-semibold">{f.replace(/_/g, " ")}</TD>
                        <TD>{b.requests}</TD>
                        <TD>{b.credits}</TD>
                        <TD>{fmtINR(b.cost_inr)}</TD>
                        <TD>{fmtINR(b.revenue_attributed)}</TD>
                        <TD className="text-emerald-600 font-bold">{fmtINR(b.profit)}</TD>
                      </TR>
                    ))}
                  </TableShell>
                )}
              </div>
            </div>
          </div>
        )
      )}

      {tab === "purchases" && (
        <div>
          <div className="flex gap-2 flex-wrap mb-4">
            {["pending", "approved", "rejected", ""].map((f) => (
              <button key={f || "all"} data-testid={`purchase-filter-${f || "all"}`} onClick={() => { setPFilter(f); setPurchases(null); loadPurchases(f); }}
                className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-colors ${pFilter === f ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {f || "all"}
              </button>
            ))}
          </div>
          {purchases === null ? <div className="bg-card border border-border rounded-2xl p-8"><LoadingRows rows={3} /></div> :
           purchases.length === 0 ? <EmptyState icon={CreditCard} title="No purchases here" body="AI credit purchases matching this filter appear here." /> : (
            <TableShell head={["Customer", "Package", "Credits", "Amount", "Reference", "Status", "Date", "Actions"]} testid="ai-purchases-table">
              {purchases.map((p) => (
                <TR key={p.id} testid={`purchase-${p.id}`}>
                  <TD><p className="font-semibold">{p.owner_name}</p><p className="text-xs text-muted-foreground">{p.owner_email}</p></TD>
                  <TD>{p.package_name}</TD>
                  <TD className="font-bold">{p.credits}</TD>
                  <TD className="font-bold">{fmtINR(p.amount)}</TD>
                  <TD className="font-mono text-xs">{p.reference || "—"}</TD>
                  <TD><StatusBadge status={p.status} /></TD>
                  <TD className="text-sm text-muted-foreground">{fmtDateTime(p.created_at)}</TD>
                  <TD>
                    {p.status === "pending" && (
                      <div className="flex gap-2">
                        <button data-testid={`purchase-approve-${p.id}`} disabled={busy} onClick={() => approve(p)}
                          className="h-8 px-3 rounded-lg bg-emerald-500/15 text-emerald-600 text-xs font-bold hover:bg-emerald-500/25">Approve</button>
                        <button data-testid={`purchase-reject-${p.id}`} disabled={busy} onClick={() => reject(p)}
                          className="h-8 px-3 rounded-lg bg-red-500/15 text-red-600 text-xs font-bold hover:bg-red-500/25">Reject</button>
                      </div>
                    )}
                  </TD>
                </TR>
              ))}
            </TableShell>
          )}
        </div>
      )}

      {tab === "packages" && (
        <div className="space-y-8">
          <form data-testid="package-create-form" onSubmit={createPkg} className="bg-card border border-border rounded-2xl p-6 flex flex-wrap gap-4 items-end">
            <div className="space-y-1.5"><Label className="overline-label">Package Name</Label>
              <Input data-testid="pkg-name" required value={pkgForm.name} onChange={(e) => setPkgForm({ ...pkgForm, name: e.target.value })} className="h-11 w-44 rounded-md bg-background" placeholder="e.g. Mega" /></div>
            <div className="space-y-1.5"><Label className="overline-label">Credits</Label>
              <Input data-testid="pkg-credits" required type="number" min="1" value={pkgForm.credits} onChange={(e) => setPkgForm({ ...pkgForm, credits: e.target.value })} className="h-11 w-28 rounded-md bg-background" /></div>
            <div className="space-y-1.5"><Label className="overline-label">Price (₹)</Label>
              <Input data-testid="pkg-price" required type="number" min="0" value={pkgForm.price} onChange={(e) => setPkgForm({ ...pkgForm, price: e.target.value })} className="h-11 w-28 rounded-md bg-background" /></div>
            <Button data-testid="pkg-create" type="submit" disabled={busy} className="rounded-full font-bold"><PlusCircle className="h-4 w-4 mr-2" /> Add Package</Button>
          </form>
          {!packages ? <div className="bg-card border border-border rounded-2xl p-8"><LoadingRows rows={3} /></div> : (
            <TableShell head={["Package", "Credits", "Price", "Sold", "Revenue", "Credits Issued", "Status", "Action"]} testid="ai-packages-table">
              {packages.map((p) => (
                <TR key={p.id} testid={`package-${p.key}`}>
                  <TD className="font-semibold">{p.name}</TD>
                  <TD className="font-bold">{p.credits}</TD>
                  <TD>{fmtINR(p.price)}</TD>
                  <TD>{p.sold}</TD>
                  <TD>{fmtINR(p.revenue)}</TD>
                  <TD>{p.credits_issued}</TD>
                  <TD><StatusBadge status={p.active ? "active" : "suspended"} /></TD>
                  <TD>
                    <button data-testid={`package-toggle-${p.key}`} onClick={() => togglePkg(p)}
                      className="h-8 px-3 rounded-lg border border-border text-xs font-bold hover:bg-muted">{p.active ? "Disable" : "Enable"}</button>
                  </TD>
                </TR>
              ))}
            </TableShell>
          )}
        </div>
      )}

      {tab === "transactions" && (
        <div>
          <div className="flex gap-2 flex-wrap mb-4 items-center">
            {["", "success", "failed"].map((f) => (
              <button key={f || "all"} data-testid={`txn-filter-${f || "all"}`} onClick={() => { setTxnStatus(f); setTxns(null); loadTxns(f); }}
                className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-colors ${txnStatus === f ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {f || "all"}
              </button>
            ))}
            <button data-testid="export-transactions" onClick={() => exportCsv("transactions")} className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-border text-xs font-bold hover:bg-muted"><Download className="h-3.5 w-3.5" /> Usage CSV</button>
            <button data-testid="export-purchases" onClick={() => exportCsv("purchases")} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-border text-xs font-bold hover:bg-muted"><Download className="h-3.5 w-3.5" /> Sales CSV</button>
          </div>
          {txns === null ? <div className="bg-card border border-border rounded-2xl p-8"><LoadingRows rows={3} /></div> :
           txns.length === 0 ? <EmptyState icon={Bot} title="No AI transactions yet" body="Every AI request is logged here with tokens, cost and credits." /> : (
            <TableShell head={["Date", "Customer", "Website", "Feature", "Model", "Credits", "Tokens", "Cost (₹)", "Est?", "Status"]} testid="ai-transactions-table">
              {txns.map((t) => (
                <TR key={t.id} testid={`txn-${t.id}`}>
                  <TD className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(t.created_at)}</TD>
                  <TD><p className="font-semibold">{t.owner_name}</p><p className="text-xs text-muted-foreground">{t.owner_email}</p></TD>
                  <TD>{t.website_name || "—"}</TD>
                  <TD>{(t.feature || "").replace(/_/g, " ")}</TD>
                  <TD className="text-xs">{t.model}</TD>
                  <TD className="font-bold">{t.credits_charged}</TD>
                  <TD className="text-xs text-muted-foreground">{t.input_tokens}/{t.output_tokens}</TD>
                  <TD>{fmtINR(t.cost_inr || 0)}</TD>
                  <TD className="text-xs">{t.estimated ? "est" : "actual"}</TD>
                  <TD><StatusBadge status={t.status} /></TD>
                </TR>
              ))}
            </TableShell>
          )}
        </div>
      )}

      {tab === "tenants" && (
        !tenants ? <div className="bg-card border border-border rounded-2xl p-8"><LoadingRows rows={3} /></div> :
        tenants.length === 0 ? <EmptyState icon={Users} title="No AI customers yet" body="Credit balances and usage per business owner appear here." /> : (
          <TableShell head={["Business Owner", "Website", "Credits Bought", "Used", "Remaining", "AI Requests", "AI Cost (₹)", "Last Activity"]} testid="ai-tenants-table">
            {tenants.map((t) => (
              <TR key={t.owner_id} testid={`tenant-${t.owner_id}`}>
                <TD><p className="font-semibold">{t.owner_name}</p><p className="text-xs text-muted-foreground">{t.owner_email}</p></TD>
                <TD>{t.website_name || "—"}</TD>
                <TD>{t.purchased}</TD>
                <TD>{t.used}</TD>
                <TD className="font-bold">{t.balance}</TD>
                <TD>{t.requests || 0}</TD>
                <TD>{fmtINR(t.cost || 0)}</TD>
                <TD className="text-xs text-muted-foreground">{t.last ? fmtDateTime(t.last) : "—"}</TD>
              </TR>
            ))}
          </TableShell>
        )
      )}

      {tab === "settings" && (
        !settings ? <div className="bg-card border border-border rounded-2xl p-8"><LoadingRows rows={3} /></div> : (
          <div className="space-y-8 max-w-4xl">
            {settings.provider && (
              <div data-testid="ai-provider-status" className={`rounded-xl border p-4 text-sm font-semibold flex items-center gap-3 ${settings.provider.openai_configured ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-600 dark:text-emerald-400" : settings.provider.emergent_configured ? "border-amber-500/30 bg-amber-500/5 text-amber-600 dark:text-amber-400" : "border-red-500/30 bg-red-500/5 text-red-600 dark:text-red-400"}`}>
                <Bot className="h-5 w-5 shrink-0" />
                {settings.provider.openai_configured
                  ? "AI Provider: MILTROO OpenAI (own API key configured ✓) — usage & cost tracked server-side"
                  : settings.provider.emergent_configured
                    ? "AI Provider: Emergent Universal Key (fallback). Set OPENAI_API_KEY in backend environment secrets to use MILTROO's own OpenAI account."
                    : "AI Provider NOT configured — set OPENAI_API_KEY in backend environment secrets, otherwise all AI operations will fail."}
              </div>
            )}
            <form data-testid="ai-settings-form" onSubmit={saveSettings} className="bg-card border border-border rounded-2xl p-6 grid sm:grid-cols-2 gap-5">
              <label className="flex items-center gap-3 cursor-pointer">
                <input data-testid="ai-set-enabled" type="checkbox" checked={Boolean(settings.enabled)} onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} className="h-5 w-5 accent-primary" />
                <span className="text-sm font-bold">AI Design Studio enabled</span>
              </label>
              <div className="space-y-1.5">
                <Label className="overline-label">Free Starter Credits (per owner, once)</Label>
                <Input data-testid="ai-set-free" type="number" min="0" value={settings.free_credits} onChange={(e) => setSettings({ ...settings, free_credits: e.target.value })} className="h-11 rounded-md bg-background" />
              </div>
              {["simple", "medium", "advanced", "major"].map((k) => (
                <div key={k} className="space-y-1.5">
                  <Label className="overline-label capitalize">{k} change credits</Label>
                  <Input data-testid={`ai-weight-${k}`} type="number" min="0" value={settings.weights?.[k] ?? 0}
                    onChange={(e) => setSettings({ ...settings, weights: { ...settings.weights, [k]: e.target.value } })} className="h-11 rounded-md bg-background" />
                </div>
              ))}
              <div className="space-y-1.5">
                <Label className="overline-label">AI Image Credits</Label>
                <Input data-testid="ai-set-image-credits" type="number" min="0" value={settings.image_credits} onChange={(e) => setSettings({ ...settings, image_credits: e.target.value })} className="h-11 rounded-md bg-background" />
              </div>
              {["image", "marketing", "content", "video", "growth"].map((k) => (
                <div key={k} className="space-y-1.5">
                  <Label className="overline-label capitalize">Growth AI — {k} credits</Label>
                  <Input data-testid={`ai-cc-${k}`} type="number" min="0" value={settings.category_credits?.[k] ?? 0}
                    onChange={(e) => setSettings({ ...settings, category_credits: { ...(settings.category_credits || {}), [k]: e.target.value } })} className="h-11 rounded-md bg-background" />
                </div>
              ))}
              <div className="space-y-1.5">
                <Label className="overline-label">Daily Request Limit (per owner)</Label>
                <Input data-testid="ai-set-daily" type="number" min="1" value={settings.daily_limit} onChange={(e) => setSettings({ ...settings, daily_limit: e.target.value })} className="h-11 rounded-md bg-background" />
              </div>
              <div className="space-y-1.5">
                <Label className="overline-label">Text Model</Label>
                <Input data-testid="ai-set-text-model" value={settings.text_model} onChange={(e) => setSettings({ ...settings, text_model: e.target.value })} className="h-11 rounded-md bg-background" />
              </div>
              <div className="space-y-1.5">
                <Label className="overline-label">Image Model</Label>
                <Input data-testid="ai-set-image-model" value={settings.image_model} onChange={(e) => setSettings({ ...settings, image_model: e.target.value })} className="h-11 rounded-md bg-background" />
              </div>
              <div className="space-y-1.5">
                <Label className="overline-label">USD → INR Rate</Label>
                <Input data-testid="ai-set-rate" type="number" step="0.01" min="0" value={settings.usd_inr_rate} onChange={(e) => setSettings({ ...settings, usd_inr_rate: e.target.value })} className="h-11 rounded-md bg-background" />
              </div>
              <div className="space-y-1.5">
                <Label className="overline-label">Credits Payment Link (SuperProfile)</Label>
                <Input data-testid="ai-set-payment-link" value={settings.payment_link} onChange={(e) => setSettings({ ...settings, payment_link: e.target.value })} className="h-11 rounded-md bg-background" placeholder="https://superprofile.bio/..." />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="overline-label">Model Pricing (JSON — USD per 1M tokens / per image)</Label>
                <textarea data-testid="ai-set-pricing" rows={5}
                  value={typeof settings.model_pricing === "string" ? settings.model_pricing : JSON.stringify(settings.model_pricing, null, 2)}
                  onChange={(e) => setSettings({ ...settings, model_pricing: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-xs font-mono" />
                <p className="text-[11px] text-muted-foreground">Historical transactions keep the pricing active at request time — editing affects only future cost calculations.</p>
              </div>
              <div className="sm:col-span-2">
                <Button data-testid="ai-settings-save" type="submit" disabled={busy} className="rounded-full font-bold">{busy ? "Saving..." : "Save AI Settings"}</Button>
              </div>
            </form>

            <form data-testid="ai-grant-form" onSubmit={doGrant} className="bg-card border border-border rounded-2xl p-6 space-y-4">
              <p className="font-display font-bold text-xl tracking-tight">Grant / Adjust Credits (manual, test mode)</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-1.5"><Label className="overline-label">Owner Email</Label>
                  <Input data-testid="grant-email" required type="email" value={grant.email} onChange={(e) => setGrant({ ...grant, email: e.target.value })} className="h-11 rounded-md bg-background" /></div>
                <div className="space-y-1.5"><Label className="overline-label">Credits (+/-)</Label>
                  <Input data-testid="grant-credits" required type="number" value={grant.credits} onChange={(e) => setGrant({ ...grant, credits: e.target.value })} className="h-11 rounded-md bg-background" /></div>
                <div className="space-y-1.5"><Label className="overline-label">Note</Label>
                  <Input data-testid="grant-note" value={grant.note} onChange={(e) => setGrant({ ...grant, note: e.target.value })} className="h-11 rounded-md bg-background" /></div>
              </div>
              <Button data-testid="grant-submit" type="submit" disabled={busy} className="rounded-full font-bold">{busy ? "Applying..." : "Apply Adjustment"}</Button>
            </form>
          </div>
        )
      )}
    </div>
  );
}
